import { TextTruncate } from "components";
import { Tooltip } from "components/common/tooltip";
import { ValidatedForm } from "components/forms";
import { ErrorPositions } from "enums/errorPositions";
import { FilterOptionsEnum } from "utils/enrollmentRulesUtils";
import { connect, type ConnectedProps } from "react-redux";
import {
  type MsGraphRule,
  defaultRule,
  getFilterOptions,
  TypeOptionsEnum,
  ruleOptions,
  columnTitles,
  getCriteriaOptions,
  ruleCombineTypeOptions,
  type RuleCombineType,
} from "./helpers";

import nameof from "utils/nameof";

import { FieldArray, FormikProvider, type FormikValues, useFormik } from "formik";
import type { RangeFilterValues } from "../../../../../utils/filterUtils";
import ExpressionBuilder from "../../../../../components/expressionBuilder/ExpressionBuilder";
import {
  type Dispatch,
  type FC,
  type MutableRefObject,
  type SetStateAction,
  type SyntheticEvent,
  useEffect,
  useImperativeHandle,
  useMemo,
} from "react";
import type { MsGraphIntegrationConfigs } from "../../types";
import { isEqual, noop } from "lodash";
import { validationSchemas } from "../../../../../utils";
import type { RootState } from "../../../../Application/globaltypes/redux";
import { bindAction } from "../../../../../interfaces";
import {
  fetchMsGraphIntegrationFilterAction,
  fetchFilterGroupAction,
} from "../../state/thunks/msGraphIntegrationFilterThunk";
import { reset } from "../../state/slices/msGraphIntegrationFilterSlice";
import { type Dispatch as AppDispatch } from "@reduxjs/toolkit";
import { Typography } from "../../../../../components/common/Typography/Typography";
import DropdownWrapper from "../../../../../components/dropdowns/dropdownWrapper/DropdownWrapper";
import { type DropdownProps, Icon } from "semantic-ui-react";
import { ruleCombineTypeTooltipMessage } from "../tooltipsHelpers";

import styles from "./MsGraphIntegrationFilterBuilder.module.scss";

export interface FilterBuilderValues extends FormikValues {
  rules: MsGraphRule[];
}
const rulesFieldName = nameof<FilterBuilderValues>("rules");
const getRulesStringKey = (i: number) => `${rulesFieldName}[${i}]`;

export type FilterBuilderRef = {
  resetForm: () => void;
};

export interface OwnProps {
  onChange?: (items: MsGraphRule[]) => void;
  configs: MsGraphIntegrationConfigs;
  setConfigs: Dispatch<SetStateAction<MsGraphIntegrationConfigs | undefined>>;
  setIsValid: (isValid: boolean) => void;
  filterBuilderFormRef?: MutableRefObject<FilterBuilderRef>;
}

export type Props = OwnProps & PropsFromRedux;

export const MsGraphIntegrationFilterBuilder: FC<Props> = ({
  setConfigs,
  setIsValid,
  filterBuilderFormRef,
  fetchMsGraphIntegrationFilter,
  fetchFilterGroup,
  reset,
  ...props
}) => {
  const filterFormik = useFormik<FilterBuilderValues>({
    initialValues: { rules: (props.configs.rules?.length && props.configs.rules) || [defaultRule] },
    validationSchema: validationSchemas.msGraphRules,
    onSubmit: noop,
    validateOnMount: true,
    enableReinitialize: false,
  });

  const { values, isValid, isValidating, errors, resetForm } = filterFormik;

  useImperativeHandle(filterBuilderFormRef, () => ({ resetForm }), [resetForm]);

  const groupIds = useMemo(() => {
    return values.rules.filter((r) => r.type === TypeOptionsEnum.Group && r.value).map((r) => r.value);
  }, [values.rules]);

  useEffect(() => {
    fetchMsGraphIntegrationFilter();
    return () => reset();
  }, [fetchMsGraphIntegrationFilter, reset]);

  useEffect(() => {
    fetchFilterGroup(groupIds);
  }, [fetchFilterGroup, groupIds]);

  useEffect(() => {
    setIsValid(!isValidating && (isValid || isEqual(values.rules, [defaultRule])));
  }, [isValid, isValidating, values.rules, errors, setConfigs, setIsValid]);

  useEffect(() => {
    setConfigs((prev) => (prev ? { ...prev, rules: values.rules } : prev));
  }, [setConfigs, values.rules]);

  const getValueField = (type: number, items: MsGraphRule[], itemIndex: number, filter: number) => {
    switch (type) {
      case TypeOptionsEnum.Department:
      case TypeOptionsEnum.JobTitle:
      case TypeOptionsEnum.Country:
      case TypeOptionsEnum.OfficeLocation:
      case TypeOptionsEnum.Manager:
      case TypeOptionsEnum.License:
      case TypeOptionsEnum.Group:
      case TypeOptionsEnum.CompanyName:
        return renderDropdownField(items, itemIndex);
      case TypeOptionsEnum.CreatedDate:
      case TypeOptionsEnum.HiredDate:
        return getCreateDateField(items, itemIndex, filter);
      default:
        return null;
    }
  };

  const getCreateDateField = (items: MsGraphRule[], itemIndex: number, filter: number) => {
    switch (filter) {
      case FilterOptionsEnum.IsBetween:
      case FilterOptionsEnum.IsNotBetween:
        return renderRangeDatePickers(items, itemIndex);
      case FilterOptionsEnum.IsOnOrAfter:
      case FilterOptionsEnum.IsOnOrBefore:
        return renderSingleDatepicker(items, itemIndex);
      default:
        return null;
    }
  };

  const updateMsGraphRule = async (
    index: number,
    updates: Partial<{ type: number | null; filter: number | null; value: string }>,
  ) => {
    await filterFormik.setValues((values) => {
      const copy = [...values.rules];
      copy[index] = { ...copy[index], ...updates };
      return { ...values, rules: copy };
    });
  };

  const handleRuleChange = async (index: number, type: number | null) => {
    await updateMsGraphRule(index, { type, filter: 0, value: "" });
  };

  const handleFilterChange = async (index: number, filter: number | null) => {
    await updateMsGraphRule(index, { filter, value: "" });
  };

  const renderDropdownField = (items: MsGraphRule[], itemIndex: number) => {
    const { errors, touched, handleChange, handleBlur, setFieldValue, setFieldTouched } = filterFormik;
    const rule = items[itemIndex];
    const rowModelName = getRulesStringKey(itemIndex);

    const dropdownOptions = getCriteriaOptions(rule.type!, rule.value, props.msGraphIntegrationFilter);
    const selected = dropdownOptions?.find((item) => item.value === rule.value);
    const displayLabel =
      (!props.msGraphIntegrationFilterLoading && typeof selected?.text === "string" && selected.text) || "Criteria";

    return (
      <ValidatedForm.DropdownField
        search
        allowAdditions
        markAsRequired
        noResultsMessage=""
        placeholder="Criteria"
        options={dropdownOptions}
        testID="criteria-dropdown"
        disabled={!rule.filter}
        propertyName={`${rowModelName}.value`}
        errorPosition={ErrorPositions.bottom}
        isLoading={props.msGraphIntegrationFilterLoading}
        value={selected?.value ?? ""}
        {...{ errors, touched, handleChange, handleBlur, setFieldValue, setFieldTouched }}
        onSearchChange={(_e: SyntheticEvent<HTMLElement>, data: { searchQuery: string }) => {
          if (rule.type === TypeOptionsEnum.Group && data.searchQuery.length > 0) {
            fetchFilterGroup(groupIds, data.searchQuery);
          }
        }}
        trigger={
          <Tooltip
            target={<TextTruncate className={styles.defaultText}>{displayLabel}</TextTruncate>}
            content={displayLabel}
          />
        }
      />
    );
  };

  const renderRangeDatePickers = (items: MsGraphRule[], itemIndex: number) => {
    const rowModelName = getRulesStringKey(itemIndex);

    return (
      <div className={styles.rangeDatePickers}>
        <ValidatedForm.DatePickerField
          placeholder="From"
          selected={(items[itemIndex].value as RangeFilterValues).from || ""}
          propertyName={`${rowModelName}.value.from`}
          markAsRequired
          disabled={items[itemIndex].filter === null}
          format="MM/DD/YYYY"
          maxDate={(items[itemIndex].value as RangeFilterValues).to}
          errorPosition={ErrorPositions.bottom}
          {...filterFormik}
        />
        <span className={styles.delimiter}>-</span>
        <ValidatedForm.DatePickerField
          placeholder="To"
          selected={(items[itemIndex].value as RangeFilterValues).to || ""}
          propertyName={`${rowModelName}.value.to`}
          markAsRequired
          minDate={(items[itemIndex].value as RangeFilterValues).from}
          disabled={items[itemIndex].filter === null}
          format="MM/DD/YYYY"
          errorPosition={ErrorPositions.bottom}
          {...filterFormik}
        />
      </div>
    );
  };

  const renderSingleDatepicker = (items: MsGraphRule[], itemIndex: number) => {
    const rowModelName = getRulesStringKey(itemIndex);

    return (
      <ValidatedForm.DatePickerField
        label=""
        placeholder="Date"
        selected={items[itemIndex].value || ""}
        propertyName={`${rowModelName}.value`}
        markAsRequired
        disabled={!items[itemIndex].filter}
        format="MM/DD/YYYY"
        errorPosition={ErrorPositions.bottom}
        {...filterFormik}
      />
    );
  };

  const getInputs = (items: MsGraphRule[], itemIndex: number) => {
    const rowModelName = getRulesStringKey(itemIndex);
    const item = items[itemIndex];

    const selectedRule = ruleOptions?.find((option) => option.value === item.type);
    const selectedRuleText = selectedRule?.text || "";

    const filterOptions = getFilterOptions(item.type as number);
    const selectedFilter = filterOptions?.find((f) => f.value === item.filter);
    const selectedFilterText = selectedFilter?.text || "";

    const renderDropdownTooltip = (value: number | null, text: string) =>
      value !== null && text ? (
        <Tooltip target={<TextTruncate className="default-text">{text}</TextTruncate>} content={text} />
      ) : null;

    return [
      <ValidatedForm.DropdownField
        placeholder="Select Rule"
        allowAdditions={false}
        value={item.type}
        minCharacters={3}
        propertyName={`${rowModelName}.type`}
        markAsRequired
        options={ruleOptions}
        testID="rule-dropdown"
        trigger={renderDropdownTooltip(item.type, selectedRuleText)}
        {...filterFormik}
        setFieldValue={(_: string, value: unknown) => handleRuleChange(itemIndex, value as number | null)}
      />,
      <ValidatedForm.DropdownField
        placeholder="Select Filter"
        value={item.filter}
        propertyName={`${rowModelName}.filter`}
        markAsRequired
        disabled={item.type === null}
        options={filterOptions}
        testID="filter-dropdown"
        trigger={renderDropdownTooltip(item.filter || null, selectedFilterText)}
        {...filterFormik}
        setFieldValue={(_: string, value: unknown) => handleFilterChange(itemIndex, value as number | null)}
      />,
      getValueField(item.type as number, items, itemIndex, item.filter as number),
    ];
  };

  const onRuleCombineTypeChange = (_: SyntheticEvent<HTMLElement, Event>, data: DropdownProps) => {
    setConfigs({ ...props.configs, ruleCombineType: data.value as RuleCombineType });
  };

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <Typography variant="title2" bold>
          FILTER IMPORTS
        </Typography>
        <div className={styles.rightBlock}>
          <Typography variant="paragraph" component="span">
            Each unique rule should be treated as an
          </Typography>
          <DropdownWrapper
            data-testid="rule-combine-type"
            className={styles.ruleCombineTypeDropdown}
            selection
            handleOptionChange={onRuleCombineTypeChange}
            selected={props.configs.ruleCombineType}
            items={ruleCombineTypeOptions}
          />
          <Typography variant="paragraph" component="span">
            statement
          </Typography>
          <Tooltip
            hoverable
            tooltipClassName="integraion-setting-tooltip"
            target={<Icon className="info circle" />}
            content={ruleCombineTypeTooltipMessage}
            position="top left"
            hideOnScroll
            showAlways
          />
        </div>
      </div>
      <ValidatedForm className={styles.form} withoutFormik fullsize disablePreventTransitionPrompt>
        <FormikProvider value={filterFormik}>
          <FieldArray
            name={rulesFieldName.toString()}
            render={() => (
              <ExpressionBuilder
                columnsAmount={3}
                onChange={(next: MsGraphRule[]) => filterFormik.setFieldValue(rulesFieldName.toString(), next, true)}
                getInputs={getInputs}
                columnTitles={columnTitles}
                items={values.rules}
                getDefaultNewItem={() => ({ ...defaultRule })}
              />
            )}
          />
        </FormikProvider>
      </ValidatedForm>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  msGraphIntegrationFilterLoading: state.accounts.integrations.base.msGraphIntegrationFilter.isLoading,
  msGraphIntegrationFilter: state.accounts.integrations.base.msGraphIntegrationFilter.data,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  fetchMsGraphIntegrationFilter: bindAction(fetchMsGraphIntegrationFilterAction, dispatch),
  fetchFilterGroup: bindAction(fetchFilterGroupAction, dispatch),
  reset: bindAction(reset, dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(MsGraphIntegrationFilterBuilder);
export default ConnectedComponent;
